import React, { memo, useRef } from "react";
import {
  Stack,
  Typography,
  Chip,
  Icon,
  useMediaQuery,
} from "investira.react.components";
import { displays } from "investira.react.lib";
import { PessoaImage } from "../../molecules";
import { validators, formats } from "investira.sdk";
import { MutedCopy } from "../../molecules";
import utils from "../../../utils";
import { ATIVO_MERCADO } from "@investirapri/financa.js/lib/enums";

const SearchPrincipalItem = memo((props) => {
  const { data, onClick } = props;

  const isUpMd = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const xId = data.pessoa_id || data.ativo_id || data.indicador_id;

  const INDICADORES_TIPO = useRef({
    M: {
      label: "Moeda",
      id: "M",
      icon: "coin",
    },
    C: {
      label: "Correção Monetária",
      id: "C",
      icon: "coins",
    },
    I: {
      label: "Índice de Mercado",
      id: "I",
      icon: "chart_line",
    },
    J: {
      label: "Juros",
      id: "J",
      icon: "percent",
    },
    T: { label: "Metal", id: "T", icon: "metal" },
  });

  const xIndicadoresTipo = INDICADORES_TIPO.current;

  const renderPessoa = (pData) => {
    const {
      pessoa_fantasia,
      pessoa,
      pessoa_tipo_id,
      cnpj,
      top_setor,
      setor,
      b3,
    } = pData;
    const xPessoaTipoList = utils.helpers.getPessoaTipoById(pessoa_tipo_id);

    return (
      <>
        <Stack flexGrow={1}>
          <Typography
            variant="body2"
            color="textPrimary"
            sx={{ fontWeight: 600 }}
          >
            {pessoa_fantasia || pessoa}
          </Typography>
          <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                display: { xs: "none", sm: "inline-block" },
              }}
            >
              {pessoa}
            </Typography>
            {cnpj && (
              <MutedCopy>
                <Typography variant="caption" color="textSecondary">
                  {`${displays.cnpj(cnpj)}`}
                </Typography>
              </MutedCopy>
            )}
          </Stack>

          <Stack
            direction="row"
            sx={{ gap: "8px 8px" }}
            flexWrap="wrap"
            spacing={{ xs: 0, sm: 1 }}
          >
            {top_setor && (
              <Chip
                label={top_setor}
                size="small"
                color="primary"
                sx={{ display: { xs: "flex", md: "none" } }}
              />
            )}
            {!validators.isEmpty(setor) && (
              <Chip
                key={setor}
                label={setor}
                size="small"
                color="secondary"
                sx={{
                  maxWidth: {
                    xs: "200px",
                    sm: "400px",
                    lg: "100%",
                  },
                }}
              />
            )}
            {xPessoaTipoList.map((xPessoaTipo) => {
              return (
                <Chip
                  key={xPessoaTipo.descricao}
                  label={xPessoaTipo.descricao}
                  size="small"
                  color="secondary"
                  sx={{ display: { xs: "none", md: "flex" } }}
                />
              );
            })}
            {b3?.codigo_emissor && (
              <Chip
                label={b3?.codigo_emissor}
                size="small"
                color="secondary"
                sx={{ display: { xs: "none", md: "flex" } }}
              />
            )}
          </Stack>
        </Stack>
        {/* {isUpMd && (
          <Stack>
            <Stack justifyContent="end">
              <Chip label={top_setor} size="small" color="primary" />
            </Stack>
          </Stack>
        )} */}
      </>
    );
  };

  const renderAtivo = (pData) => {
    const {
      ticker,
      ativo_seuid,
      ativo,
      isin,
      mercado,
      emissor_cnpj,
      vencimento,
      serie,
    } = pData;

    return (
      <Stack flexGrow={1}>
        <Typography
          variant="body2"
          color="textPrimary"
          sx={{ fontWeight: 600 }}
        >
          {ticker || ativo_seuid || ativo}
        </Typography>
        <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{
              display: { xs: "none", sm: "inline-block" },
            }}
          >
            {ativo || isin}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{ gap: "8px 8px" }}
          flexWrap="wrap"
          gap={{ xs: 0, sm: 1 }}
        >
          {mercado && (
            <Chip
              label={ATIVO_MERCADO[mercado].descricao}
              size="small"
              color="primary"
              sx={{ display: { xs: "flex", md: "none" } }}
            />
          )}

          {emissor_cnpj && (
            <Chip
              label={displays.cnpj(emissor_cnpj)}
              size="small"
              color="secondary"
            />
          )}

          {isin && <Chip label={isin} size="small" color="secondary" />}
          {vencimento && (
            <Chip
              label={`Venc.: ${formats.formatDate(vencimento)}`}
              size="small"
              color="secondary"
            />
          )}
          {serie && (
            <Chip label={`Série: ${serie}`} size="small" color="secondary" />
          )}
        </Stack>
      </Stack>
    );
  };

  const renderIndicador = (pData) => {
    const { indicador_id, indicador, indicador_tipo_id } = pData;

    return (
      <Stack flexGrow={1}>
        <Typography
          variant="body2"
          color="textPrimary"
          sx={{ fontWeight: 600 }}
        >
          {indicador_id}
        </Typography>
        <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{
              display: { xs: "none", sm: "inline-block" },
            }}
          >
            {indicador}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{ gap: "8px 8px" }}
          flexWrap="wrap"
          spacing={{ xs: 0, sm: 1 }}
        >
          <Chip
            label={xIndicadoresTipo[indicador_tipo_id].label}
            size="small"
            color="primary"
            //sx={{ display: { xs: "flex", md: "none" } }}
          />
        </Stack>
      </Stack>
    );
  };

  const render = {
    pessoa: renderPessoa,
    ativo: renderAtivo,
    indicador: renderIndicador,
  };

  return (
    <Stack
      id={xId}
      key={xId}
      direction="row"
      flexGrow={1}
      spacing={3}
      alignItems="flex-start"
      component="li"
      onClick={onClick}
      sx={{
        px: 2,
        py: 2,
        cursor: "pointer",
        "&:hover": {
          color: "gray",
          backgroundColor: "background.main",
        },
        borderBottom: "1px solid",
        borderBottomColor: "background.main",
      }}
    >
      <Stack>
        {data.pesquisa_origem === "indicador" ? (
          <Stack
            backgroundColor="primary.main"
            width="36px"
            height="36px"
            justifyContent="center"
            alignItems="center"
            borderRadius="8px"
          >
            <Icon
              iconName={xIndicadoresTipo[data.indicador_tipo_id].icon}
              color="secondary"
              size={18}
            />
          </Stack>
        ) : (
          <PessoaImage
            logoPessoaId={data.logo_pessoa_id}
            alt={data.pessoa || data.ativo || data.indicador}
            hasLogo={data.logo_ok}
            size={36}
          />
        )}
      </Stack>

      {render[data.pesquisa_origem](data)}
    </Stack>
  );
});

export default SearchPrincipalItem;
